import { Download, FileSignature } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/common/loader";
import {
  downloadUsageReportCsv,
  getUsageReport,
} from "@/features/esignatures/api";
import type { UsageReport } from "@/features/esignatures/types";
import { ApiError } from "@/lib/api";

type Preset = "this_month" | "last_month" | "this_year" | "custom";

/** Resolve a preset to a {from,to} ISO date window (empty = all-time). */
function presetRange(preset: Preset, from: string, to: string): { from?: string; to?: string } {
  const now = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  if (preset === "this_month") {
    return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
  }
  if (preset === "last_month") {
    return {
      from: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      to: iso(new Date(now.getFullYear(), now.getMonth(), 0)),
    };
  }
  if (preset === "this_year") {
    return { from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(now) };
  }
  return { from: from || undefined, to: to || undefined };
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function ESignaturesReportPage() {
  const [preset, setPreset] = useState<Preset>("this_month");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [report, setReport] = useState<UsageReport | null>(null);
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => presetRange(preset, from, to), [preset, from, to]);

  useEffect(() => {
    setLoading(true);
    getUsageReport(range)
      .then(setReport)
      .catch((err) =>
        toast.error(err instanceof ApiError ? err.message : "Couldn't load the report."),
      )
      .finally(() => setLoading(false));
  }, [range]);

  const handleExport = () => {
    downloadUsageReportCsv(range).catch(() =>
      toast.error("Couldn't export the report."),
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-foreground mb-2 flex items-center gap-2 text-3xl font-bold">
            <FileSignature className="text-primary size-7" /> E-Signature Usage
          </h1>
          <p className="text-muted-foreground">
            {report ? `${report.remaining} of ${report.quota} requests remaining` : " "}
          </p>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={!report}>
          <Download /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="flex flex-wrap items-end gap-4 p-4">
        <div className="grid gap-1.5">
          <Label>Date range</Label>
          <Select value={preset} onValueChange={(v) => setPreset(v as Preset)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {preset === "custom" && (
          <>
            <div className="grid gap-1.5">
              <Label htmlFor="from">From</Label>
              <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="to">To</Label>
              <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </>
        )}
      </Card>

      <Card className="gap-0 overflow-hidden p-0">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : !report || report.rows.length === 0 ? (
          <p className="text-muted-foreground p-6 text-sm">
            No signature requests in this period.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Requestor</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Document</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDate(row.createdAt)}
                  </TableCell>
                  <TableCell className="text-sm">{row.requestor}</TableCell>
                  <TableCell className="text-sm">{row.client}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{row.recipient}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{row.document}</TableCell>
                  <TableCell className="text-sm">{row.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
